import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { BASE_URL } from "../../config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBan,
  faSearch,
  faSyncAlt,
  faPlus,
  faEye,
  faAngleLeft,
  faAngleRight,
} from "@fortawesome/free-solid-svg-icons";

const getAuthHeaders = () => {
  const token =
    localStorage.getItem("authentication/token") ||
    localStorage.getItem("auth_token") ||
    "";

  return token
    ? {
        Authorization: `Bearer ${token}`,
      }
    : {};
};

const formatCurrency = (amount) => {
  const numeric = Number(amount || 0);
  return new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency: "PHP",
    maximumFractionDigits: 2,
  }).format(numeric);
};

const getStatusUi = (item) => {
  const code = String(item?.trans_flag_status || "").trim();
  const desc = String(item?.trans_flag_desc || "").toLowerCase();

  const isRejected = desc.includes("reject");
  const isCancelled = code === "04" || desc.includes("cancel");
  const isProcessed = code === "06" || desc.includes("process");
  const isInvalidDocs = code === "07" || desc.includes("invalid");
  const isEndorsed = code === "11" || desc.includes("endorse");
  const isSaved = code === "10" || desc.includes("saved") || desc.includes("received");

  if (isRejected) {
    return {
      row: "bg-red-50/80 hover:bg-red-100/70",
      badge: "bg-red-200 text-red-900 border border-red-300",
    };
  }

  if (isCancelled) {
    return {
      row: "bg-rose-50/80 hover:bg-rose-100/70",
      badge: "bg-rose-200 text-rose-900 border border-rose-300",
    };
  }

  if (isProcessed) {
    return {
      row: "bg-green-50/80 hover:bg-green-100/70",
      badge: "bg-green-200 text-green-900 border border-green-300",
    };
  }

  if (isInvalidDocs) {
    return {
      row: "bg-orange-50/80 hover:bg-orange-100/70",
      badge: "bg-orange-200 text-orange-900 border border-orange-300",
    };
  }

  if (isEndorsed) {
    return {
      row: "bg-blue-50/80 hover:bg-blue-100/70",
      badge: "bg-blue-200 text-blue-900 border border-blue-300",
    };
  }

  if (isSaved) {
    return {
      row: "bg-amber-50/80 hover:bg-amber-100/70",
      badge: "bg-amber-200 text-amber-900 border border-amber-300",
    };
  }

  return {
    row: "bg-gray-50/50 hover:bg-gray-100/70",
    badge: "bg-gray-200 text-gray-900 border border-gray-300",
  };
};

const ReimbursementList = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState({
    total: 0,
    total_pages: 1,
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);

    return () => clearTimeout(timer);
  }, [search]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/reimbursement/transactions/filterable`,
        {
          params: {
            search: debouncedSearch,
            page,
            limit,
          },
          headers: getAuthHeaders(),
        }
      );

      const payload = response?.data?.data || {};
      setRows(payload.transactions || []);
      setPagination(
        payload.pagination || {
          total: 0,
          total_pages: 1,
        }
      );
    } catch (error) {
      if (error?.response?.status === 401) {
        window.location.href = "/login";
      }
      setRows([]);
      setPagination({ total: 0, total_pages: 1 });
    } finally {
      setLoading(false);
    }
  };

  const getLoggedUser = () =>
    localStorage.getItem("name") ||
    localStorage.getItem("username") ||
    localStorage.getItem("user_name") ||
    "Web Portal";

  const handleCancelFromList = async (transNo) => {
    const result = await Swal.fire({
      icon: "warning",
      title: "Cancel Reimbursement?",
      html: "<p>Please provide a reason for cancellation:</p>",
      input: "textarea",
      inputLabel: "Cancellation Reason",
      inputPlaceholder: "Enter reason for cancellation...",
      showCancelButton: true,
      confirmButtonText: "Yes, Cancel Transaction",
      cancelButtonText: "Keep Transaction",
      confirmButtonColor: "#dc2626",
      inputValidator: (value) => {
        if (!value || !value.trim()) return "Please enter a cancellation reason.";
      },
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      await axios.put(
        `${BASE_URL}/reimbursement/transactions/${encodeURIComponent(transNo)}/cancel`,
        {
          cancelled_details: result.value,
          cancelled_by: getLoggedUser(),
        },
        {
          headers: getAuthHeaders(),
        }
      );

      await Swal.fire({
        icon: "success",
        title: "Transaction cancelled",
        timer: 1400,
        showConfirmButton: false,
      });

      fetchTransactions();
    } catch (error) {
      if (error?.response?.status === 401) {
        window.location.href = "/login";
        return;
      }

      Swal.fire({
        icon: "error",
        title: "Failed to cancel",
        text: error?.response?.data?.message || "Unable to cancel transaction.",
      });
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [page, limit, debouncedSearch]);

  const totalPages = useMemo(
    () => Math.max(1, Number(pagination?.total_pages || 1)),
    [pagination]
  );

  const startRow = useMemo(() => {
    if (!rows.length) return 0;
    return (page - 1) * limit + 1;
  }, [rows, page, limit]);

  const endRow = useMemo(() => {
    if (!rows.length) return 0;
    return Math.min(page * limit, Number(pagination?.total || 0));
  }, [rows, page, limit, pagination]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50 p-4 md:p-6">
      <div className="mx-auto w-full max-w-7xl space-y-6">
        <div className="rounded-2xl border border-orange-100 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Reimbursement Transactions</h1>
              <p className="text-sm text-gray-500">
                Review and manage reimbursement requests.
              </p>
            </div>
            <button
              onClick={() => navigate("/transaction-reimbursement-add")}
              className="inline-flex items-center gap-2 rounded-lg bg-orange-600 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-700"
            >
              <FontAwesomeIcon icon={faPlus} />
              Add Reimbursement
            </button>
          </div>
        </div>

        <div className="rounded-2xl border border-orange-100 bg-white p-5 shadow-sm">
          <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="relative w-full md:max-w-md">
              <FontAwesomeIcon
                icon={faSearch}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by transaction #, member, provider, corporate"
                className="w-full rounded-lg border border-gray-200 py-2 pl-10 pr-3 text-sm focus:border-orange-500 focus:outline-none"
              />
            </div>

            <div className="flex items-center gap-2">
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                }}
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none"
              >
                <option value={10}>10 / page</option>
                <option value={25}>25 / page</option>
                <option value={50}>50 / page</option>
              </select>

              <button
                onClick={fetchTransactions}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <FontAwesomeIcon icon={faSyncAlt} />
                Refresh
              </button>
            </div>
          </div>

          <div className="overflow-x-auto rounded-xl border border-gray-100">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">TRN #</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Date</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Member</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Corporate</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Provider</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Coverage</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Status</th>
                  <th className="px-3 py-2 text-right font-semibold text-gray-700">Claim Amount</th>
                  <th className="px-3 py-2 text-center font-semibold text-gray-700">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {loading && (
                  [...Array(8)].map((_, idx) => (
                    <tr key={`skeleton-row-${idx}`} className="animate-pulse">
                      <td className="px-3 py-3">
                        <div className="h-3 w-24 rounded bg-gray-200" />
                      </td>
                      <td className="px-3 py-3">
                        <div className="h-3 w-20 rounded bg-gray-200" />
                      </td>
                      <td className="px-3 py-3">
                        <div className="h-3 w-36 rounded bg-gray-200" />
                      </td>
                      <td className="px-3 py-3">
                        <div className="h-3 w-32 rounded bg-gray-200" />
                      </td>
                      <td className="px-3 py-3">
                        <div className="h-3 w-32 rounded bg-gray-200" />
                      </td>
                      <td className="px-3 py-3">
                        <div className="h-3 w-24 rounded bg-gray-200" />
                      </td>
                      <td className="px-3 py-3">
                        <div className="h-5 w-28 rounded-full bg-gray-200" />
                      </td>
                      <td className="px-3 py-3 text-right">
                        <div className="ml-auto h-3 w-24 rounded bg-gray-200" />
                      </td>
                      <td className="px-3 py-3">
                        <div className="mx-auto h-7 w-24 rounded bg-gray-200" />
                      </td>
                    </tr>
                  ))
                )}

                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-3 py-10 text-center text-gray-500">
                      No reimbursement transactions found.
                    </td>
                  </tr>
                )}

                {!loading &&
                  rows.map((item) => {
                    const statusUi = getStatusUi(item);

                    return (
                    <tr key={item.trans_no} className={statusUi.row}>
                      <td className="px-3 py-2 font-semibold text-gray-900">{item.trans_no}</td>
                      <td className="px-3 py-2 text-gray-700">{item.trans_date || "-"}</td>
                      <td className="px-3 py-2 text-gray-700">{item.full_name || "-"}</td>
                      <td className="px-3 py-2 text-gray-700">{item.corp_name || "-"}</td>
                      <td className="px-3 py-2 text-gray-700">{item.provider_name || "-"}</td>
                      <td className="px-3 py-2 text-gray-700">{item.coverage_code || "-"}</td>
                      <td className="px-3 py-2 text-gray-700">
                        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${statusUi.badge}`}>
                          {item.trans_flag_desc || item.trans_flag_status || "-"}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right text-gray-700">
                        {formatCurrency(item.total_amt_of_claim)}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() =>
                              navigate(
                                `/transaction-reimbursement-add?trans_no=${encodeURIComponent(
                                  item.trans_no
                                )}`
                              )
                            }
                            className="inline-flex items-center gap-1 rounded-md border border-orange-200 px-3 py-1.5 text-xs font-semibold text-orange-700 hover:bg-orange-100"
                          >
                            <FontAwesomeIcon icon={faEye} />
                            View
                          </button>

                          {item.trans_flag_status !== "06" && item.trans_flag_status !== "04" ? (
                            <button
                              onClick={() => handleCancelFromList(item.trans_no)}
                              className="inline-flex items-center gap-1 rounded-md border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50"
                            >
                              <FontAwesomeIcon icon={faBan} />
                              Cancel
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <p className="text-sm text-gray-600">
              Showing {startRow} to {endRow} of {Number(pagination?.total || 0)} records
            </p>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={page <= 1 || loading}
                className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-2 text-sm disabled:opacity-50"
              >
                <FontAwesomeIcon icon={faAngleLeft} /> Prev
              </button>

              <span className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700">
                Page {page} of {totalPages}
              </span>

              <button
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={page >= totalPages || loading}
                className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-2 text-sm disabled:opacity-50"
              >
                Next <FontAwesomeIcon icon={faAngleRight} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReimbursementList;
